/**
 * Organization Permission Adapter
 * Wraps OrganizationService methods to provide unified permission interface
 */
import { ROLES } from '../constants';
import OrganizationService from '../../OrganizationService';

// Cache for OrgRoles mapping (keyed by organizationId)
const orgRolesCache = {};

/**
 * Get OrgRoles mapping from all available roles
 * Fetches all org roles to ensure we have both owner and viewer role IDs
 */
async function getOrgRolesMapping(organizationId) {
  if (orgRolesCache[organizationId]) {
    return orgRolesCache[organizationId];
  }

  try {
    // Fetch all available organization roles (owner, viewer)
    const orgRoles = await OrganizationService.getOrganizationRoles(organizationId);
    
    // Build mapping from access to id
    const orgRolesMap = {};
    orgRoles.forEach((role) => {
      if (role.access && role.id) {
        orgRolesMap[role.access] = role.id;
      }
    });
    
    orgRolesCache[organizationId] = orgRolesMap;
    
    return orgRolesCache[organizationId];
  } catch (error) {
    console.error('Failed to get OrgRoles mapping:', error);
    // Return empty mapping if fetch fails
    return {};
  }
}

const OrgPermissionAdapter = {
  /**
   * Get all users in an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of organization users with User data
   */
  async getUsers(organizationId, options = {}) {
    const includeInactive = options.includeInactive === true;
    const orgUsers = await OrganizationService.getOrganizationUsers(
      organizationId,
      { includeInactive }
    );
    // Transform to unified format
    return orgUsers.map((user) => ({
      id: user.id,
      userId: user.id,
      resourceId: organizationId,
      resourceType: 'organization',
      role: user.orgRole?.access || 'viewer', // Map orgRole access to role
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        photoPath: user.photoPath,
        title: user.title,
        isActive: user.isActive,
      },
      // Keep original data for compatibility
      _original: user,
    }));
  },

  /**
   * Assign role to user(s) in an organization
   * @param {string} organizationId - Organization ID
   * @param {string|Array<string>} emails - Email(s) of user(s) to assign
   * @param {string} role - Role to assign (owner, viewer)
   * @param {Object} options - Additional options
   * @param {string} options.message - Optional message
   * @returns {Promise<Object>} Response data
   */
  async assignRole(organizationId, emails, role, options = {}) {
    const emailsList = Array.isArray(emails) ? emails : [emails];
    
    // Get OrgRoles mapping to convert role name to orgRoleId
    const orgRolesMapping = await getOrgRolesMapping(organizationId);
    const orgRoleId = orgRolesMapping[role];
    
    if (!orgRoleId) {
      throw new Error(`Invalid organization role: ${role}`);
    }
    
    return OrganizationService.addOrganizationUser({
      organizationId,
      emails: emailsList,
      orgRoleId,
      message: options.message || '',
      newUsers: options.newUsers || null,
      folderIds: options.folderIds || null,
    });
  },

  /**
   * Update user's role in an organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @param {string} role - New role (owner, viewer)
   * @returns {Promise<Object>} Updated organization user data
   */
  async updateRole(organizationId, userId, role) {
    // Get OrgRoles mapping to convert role name to orgRoleId
    const orgRolesMapping = await getOrgRolesMapping(organizationId);
    const orgRoleId = orgRolesMapping[role];
    
    if (!orgRoleId) {
      throw new Error(`Invalid organization role: ${role}`);
    }
    
    return OrganizationService.updateOrganizationUser({
      organizationId,
      userId,
      orgRoleId,
    });
  },

  /**
   * Remove user from an organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  removeUser(organizationId, userId) {
    return OrganizationService.removeOrganizationUser({
      organizationId,
      userId,
    });
  },

  /**
   * Reactivate a user in an organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  reactivateUser(organizationId, userId) {
    return OrganizationService.updateOrganizationUser({
      organizationId,
      userId,
      isActive: true,
    });
  },

  /**
   * Resend organization invitation email to a user
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response data
   */
  resendInvitation(organizationId, userId) {
    return OrganizationService.resendOrganizationInvite({
      organizationId,
      userId,
    });
  },

  /**
   * Get available roles for organization
   * @returns {Array<string>} Available roles
   */
  getAvailableRoles() {
    return [ROLES.OWNER, ROLES.VIEWER];
  },
};

export default OrgPermissionAdapter;

